import React from "react";
import { TouchableOpacity, View, StyleSheet } from "react-native";

import CustomText from "../../general/CustomText/CustomText";

interface RadioButtonProps {
  checked: boolean;
  onPress: () => void;
  label?: string;
  disabled?: boolean;
  size?: "small" | "medium" | "large";
  color?: string;
  style?: any;
  labelStyle?: any;
}

const RadioButton: React.FC<RadioButtonProps> = ({
  checked,
  onPress,
  label,
  disabled = false,
  size = "medium",
  color = "#3E68FE",
  style,
  labelStyle,
}) => {
  const getSizeStyles = () => {
    switch (size) {
      case "small":
        return {
          container: { width: 16, height: 16 },
          inner: { width: 8, height: 8, borderRadius: 4 },
        };
      case "large":
        return {
          container: { width: 24, height: 24 },
          inner: { width: 12, height: 12, borderRadius: 6 },
        };
      default: // medium
        return {
          container: { width: 20, height: 20 },
          inner: { width: 10, height: 10, borderRadius: 5 },
        };
    }
  };

  const sizeStyles = getSizeStyles();

  const radioButtonStyle = [
    styles.radioButton,
    sizeStyles.container,
    checked && { borderColor: color },
    disabled && styles.disabled,
    style,
  ];

  const innerStyle = [sizeStyles.inner, checked && { backgroundColor: color }];

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="radio"
      accessibilityState={{ checked, disabled }}
      accessibilityLabel={label || "Radio button"}
      role="radio"
      aria-checked={checked}
      aria-disabled={disabled}
      aria-label={label || "Radio button"}
      style={[styles.container, disabled && styles.disabledContainer]}
      activeOpacity={0.7}>
      <View style={radioButtonStyle}>{checked && <View style={innerStyle} />}</View>
      {label && (
        <CustomText text={label} size="s" style={[styles.label, disabled && styles.disabledLabel, labelStyle]} />
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  radioButton: {
    borderWidth: 2,
    borderColor: "#D1D5DB",
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
  },
  disabled: {
    borderColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
  },
  disabledContainer: {
    opacity: 0.6,
  },
  label: {
    color: "#374151",
    flex: 1,
  },
  disabledLabel: {
    color: "#9CA3AF",
  },
});

export default RadioButton;
